// src/pages/SubjectsPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';
import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';


import {
  fetchSubjects,
  createSubject,
  updateSubject,
  deleteSubject,
} from '../controllers/subject.controller';

import { getAllProjects } from '../controllers/project.controller';
import { getClinicsByProject2 } from '../controllers/clinic.controller';

import { useNavigate } from 'react-router-dom';

const SubjectsPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    projectId: '',
    clinicId: '',
    gender: '',
    startDate: null, // DateObject از date-picker
    endDate: null,   // DateObject
    page: 1,
    limit: 20,
  });


  const [subjects, setSubjects] = useState([]);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});

  // برای فرم
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    nationalId: '',
    birthDate: '',
    gender: '',
    projectId: '',
    clinicId: '',
    description: '',
  });

  const [projects, setProjects] = useState([]);
  const [clinics, setClinics] = useState([]);



  // 1) گرفتن لیست اولیه
  useEffect(() => {
    loadSubjects();
    loadProjects();
  }, [
    filters.search,
    filters.projectId,
    filters.clinicId,
    filters.gender,
    filters.startDate,
    filters.endDate,
    filters.page,
    filters.limit,
  ]);

  const loadSubjects = async () => {
    try {
      const params = {
        search: filters.search || '',
        projectId: filters.projectId || '',
        clinicId: filters.clinicId || '',
        gender: filters.gender || '',
        startDate: persianDateObjectToISOStart(filters.startDate),
        endDate: persianDateObjectToISOEnd(filters.endDate),
        page: filters.page,
        limit: filters.limit,
      };

      const res = await fetchSubjects(params);
      setSubjects(res.items || []);
      // اگر paging UI می‌خوای:
      // setTotal(res.total || 0);
    } catch (err) {
      console.error('❌ خطا در loadSubjects:', err.response?.data || err.message);

      alert(
        err.response?.data?.message ||
        `خطا در دریافت لیست بیماران (کد: ${err.response?.status || 'نامشخص'})`
      );
    }
  };


  const loadProjects = async () => {
    try {
      const res = await getAllProjects();
      setProjects(res || []);
    } catch (err) {
      console.error('❌ خطا در loadProjects:', err.response?.data || err.message);
      alert(
        err.response?.data?.message ||
        'خطا در دریافت لیست پروژه‌ها'
      );
    }
  };


  const loadClinics = async (projectId) => {
    try {
      if (!projectId) {
        setClinics([]);
        return;
      }

      const res = await getClinicsByProject2(projectId);  // ⬅️ فقط کلینیک‌ها
      setClinics(res || []);
    } catch (e) {
      console.error('❌ خطا در loadClinics:', e);
      alert(
        e.response?.data?.message ||
        'خطا در دریافت لیست کلینیک‌ها'
      );
    }
  };




  const handleOpenNew = () => {
    setEditing(null);
    setForm({
      firstName: '',
      lastName: '',
      nationalId: '',
      birthDate: '',
      gender: '',
      projectId: '',
      clinicId: '',
      description: '',
    });
    setErrors({});
    setOpenDialog(true);
  };

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      firstName: row.firstName || '',
      lastName: row.lastName || '',
      nationalId: row.nationalId || '',
      birthDate: row.birthDate ? row.birthDate.substring(0, 10) : '',
      gender: row.gender || '',
      projectId: row.projectId || '',
      clinicId: row.clinicId || '',
      description: row.description || '',
    });
    setErrors({});
    // وقتی ادیت می‌کنیم، کلینیک‌های پروژه‌ی خودش رو هم لود کنیم
    if (row.projectId) {
      loadClinics(row.projectId);
    }
    setOpenDialog(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm('حذف شود؟')) return;

    try {
      await deleteSubject(row._id);
      await loadSubjects();
    } catch (err) {
      console.error('❌ خطا در حذف بیمار:', err.response?.data || err.message);
      alert(
        err.response?.data?.message ||
        'خطا در حذف بیمار'
      );
    }
  };


  const handleSave = async () => {
    const newErrors = {};

    if (!form.firstName.trim()) newErrors.firstName = 'نام الزامی است';
    if (!form.lastName.trim()) newErrors.lastName = 'نام خانوادگی الزامی است';

    const nationalIdClean = form.nationalId
      ? toEnglishDigits(form.nationalId).trim()
      : '';

    if (!nationalIdClean) {
      newErrors.nationalId = 'کد ملی الزامی است';
    } else if (!isValidIranianNationalId(nationalIdClean)) {
      newErrors.nationalId = 'کد ملی نامعتبر است';
    }

    if (!form.birthDate) newErrors.birthDate = 'تاریخ تولد الزامی است';
    if (!form.gender) newErrors.gender = 'جنسیت را انتخاب کنید';
    if (!form.projectId) newErrors.projectId = 'پروژه را انتخاب کنید';
    if (!form.clinicId) newErrors.clinicId = 'کلینیک را انتخاب کنید';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({}); // ولیدیشن OK

    // حتماً نسخه استاندارد کد ملی را بفرستیم
    const payload = {
      ...form,
      nationalId: nationalIdClean,
    };

    try {
      if (editing) {
        await updateSubject(editing._id, payload);
      } else {
        await createSubject(payload);
      }
      setOpenDialog(false);
      await loadSubjects();
    } catch (err) {
      console.error('❌ خطا در ذخیره اطلاعات بیمار:', err.response?.data || err.message);
      alert(
        err.response?.data?.message ||
        'خطا در ذخیره اطلاعات بیمار'
      );
    }
  };


  const handleProjectChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, projectId: value, clinicId: '' }));
    loadClinics(value);
  };


  // فقط رقم، حداکثر ۱۰ کاراکتر
  const normalizeNationalIdInput = (value = '') => {
    // تبدیل ارقام فارسی به انگلیسی
    const english = toEnglishDigits(value);
    // حذف هرچیزی غیر از رقم و محدود کردن به 10 رقم
    return english.replace(/\D/g, '').slice(0, 10);
  };

  const toEnglishDigits = (str) => str.replace(/[۰-۹]/g, d => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
  // ولیدیشن کد ملی ایران
  const isValidIranianNationalId = (rawCode) => {
    if (!rawCode) return false;

    const code = toEnglishDigits(rawCode).trim();

    // باید دقیقاً 10 رقم باشد
    if (!/^\d{10}$/.test(code)) return false;

    // رد کردن کدهای تکراری مثل 0000000000 یا 1111111111
    if (/^(\d)\1{9}$/.test(code)) return false;

    const digits = code.split('').map(Number);
    const check = digits[9];

    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += digits[i] * (10 - i);
    }

    const r = sum % 11;

    return (r < 2 && check === r) || (r >= 2 && check === 11 - r);
  };

  const persianDateObjectToISOStart = (dateObj) => {
    if (!dateObj) return '';
    const d = dateObj.toDate();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const persianDateObjectToISOEnd = (dateObj) => {
    if (!dateObj) return '';
    const d = dateObj.toDate();
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  };

  return (
    <Box p={3} dir="rtl">
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={2}>
        <TextField
          size="small"
          label="جستجو (نام/نام‌خانوادگی/کدملی)"
          value={filters.search}
          onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value, page: 1 }))}
          sx={{ minWidth: 260 }}
        />

        <TextField
          select
          size="small"
          label="پروژه"
          value={filters.projectId}
          onChange={(e) => {
            const projectId = e.target.value;
            setFilters((p) => ({ ...p, projectId, clinicId: '', page: 1 }));
            loadClinics(projectId);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">همه</MenuItem>
          {projects.map((p) => (
            <MenuItem key={p._id} value={p._id}>
              {p.title?.fa || p.title?.en || 'پروژه'}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="کلینیک"
          value={filters.clinicId}
          onChange={(e) => setFilters((p) => ({ ...p, clinicId: e.target.value, page: 1 }))}
          sx={{ minWidth: 180 }}
          disabled={!filters.projectId}
        >
          <MenuItem value="">همه</MenuItem>
          {clinics.map((c) => (
            <MenuItem key={c._id} value={c._id}>
              {c.title?.fa || c.title?.en || 'کلینیک'}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="جنسیت"
          value={filters.gender}
          onChange={(e) => setFilters((p) => ({ ...p, gender: e.target.value, page: 1 }))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">همه</MenuItem>
          <MenuItem value="male">مذکر</MenuItem>
          <MenuItem value="female">مونث</MenuItem>
        </TextField>

        {/* تاریخ شمسی */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end' }}>
          <Box sx={{ minWidth: 170 }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 0.5, fontSize: 13, fontWeight: 600 }}
            >
              از تاریخ (ثبت)
            </Typography>

            <DatePicker
              calendar={persian}
              locale={persian_fa}
              format="YYYY/MM/DD"
              value={filters.startDate}
              onChange={(date) => setFilters((p) => ({ ...p, startDate: date || null, page: 1 }))}
              style={{ width: 170, height: 40 }}
            />
          </Box>

          <Box sx={{ minWidth: 170 }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 0.5, fontSize: 13, fontWeight: 600 }}
            >
              تا تاریخ (ثبت)
            </Typography>

            <DatePicker
              calendar={persian}
              locale={persian_fa}
              format="YYYY/MM/DD"
              value={filters.endDate}
              onChange={(date) => setFilters((p) => ({ ...p, endDate: date || null, page: 1 }))}
              style={{ width: 170, height: 40 }}
            />
          </Box>
        </Box>

        <Button variant="outlined" onClick={loadSubjects}>
          اعمال فیلتر
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            setClinics([]);
            setFilters({
              search: '',
              projectId: '',
              clinicId: '',
              gender: '',
              startDate: null,
              endDate: null,
              page: 1,
              limit: 20,
            });
          }}
        >
          پاک کردن
        </Button>


        <Button variant="contained" onClick={handleOpenNew}>
          بیمار جدید
        </Button>
      </Box>


      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>نام</TableCell>
              <TableCell>نام خانوادگی</TableCell>
              <TableCell>کدملی</TableCell>
              <TableCell>پروژه</TableCell>
              <TableCell>کلینیک</TableCell>
              <TableCell align="center">عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.map((row) => (
              <TableRow key={row._id}>
                <TableCell>{row.firstName}</TableCell>
                <TableCell>{row.lastName}</TableCell>
                <TableCell>{row.nationalId}</TableCell>
                <TableCell>{row.projectTitle?.fa || row.projectTitle?.en || '—'}</TableCell>
                <TableCell>{row.clinicTitle?.fa || row.clinicTitle?.en || '—'}</TableCell>

                <TableCell align="center">
                  <IconButton color="primary" onClick={() => handleEdit(row)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(row)}>
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    onClick={() => navigate(`/subjects/${row._id}/responses`)}
                    title="پاسخ‌های این بیمار"
                  >
                    <ListIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {subjects.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  موردی یافت نشد
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* دیالوگ افزودن/ویرایش */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm" dir="rtl">
        <DialogTitle>{editing ? 'ویرایش بیمار' : 'بیمار جدید'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="نام"
            value={form.firstName}
            onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
            fullWidth
            error={!!errors.firstName}             // ⬅️
            helperText={errors.firstName || ''}    // ⬅️
          />
          <TextField
            label="نام خانوادگی"
            value={form.lastName}
            onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName || ''}
          />
          <TextField
            label="کد ملی"
            value={form.nationalId}
            onChange={(e) => {
              const clean = normalizeNationalIdInput(e.target.value);

              setForm((prev) => ({
                ...prev,
                nationalId: clean,
              }));

              // اگر قبلاً خطای کد ملی داشتیم و کاربر در حال اصلاح است، خطا را پاک کنیم
              if (errors.nationalId) {
                setErrors((prev) => ({
                  ...prev,
                  nationalId: '',
                }));
              }
            }}
            fullWidth
            error={!!errors.nationalId}
            helperText={errors.nationalId || ''}
          />


          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 0.5 }}
            >
              تاریخ تولد (شمسی)
            </Typography>

            <DatePicker
              calendar={persian}
              locale={persian_fa}
              format="YYYY/MM/DD"
              value={
                form.birthDate
                  ? new DateObject({
                    date: form.birthDate,
                    calendar: persian,
                    format: "YYYY/MM/DD"
                  })
                  : null
              }
              onChange={(date) => {
                if (!date) {
                  // کاربر تاریخ را پاک کرده
                  setForm((prev) => ({ ...prev, birthDate: '' }));
                  return;
                }

                const formatted = date.format('YYYY/MM/DD');  // خروجی با ارقام فارسی
                const english = toEnglishDigits(formatted);   // تبدیل به انگلیسی

                setForm((prev) => ({
                  ...prev,
                  birthDate: english,
                }));
              }}
              containerStyle={{ width: '100%' }}
              style={{ width: '100%', height: 40 }}
            />
            {errors.birthDate && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.birthDate}
              </Typography>
            )}
          </Box>


          <TextField
            select
            label="جنسیت"
            value={form.gender}
            onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
            fullWidth
            error={!!errors.gender}
            helperText={errors.gender || ''}
          >
            <MenuItem value="">نامشخص</MenuItem>
            <MenuItem value="male">مذکر</MenuItem>
            <MenuItem value="female">مونث</MenuItem>
          </TextField>

          <TextField
            select
            label="پروژه"
            value={form.projectId}
            onChange={handleProjectChange}
            fullWidth
            error={!!errors.projectId}
            helperText={errors.projectId || ''}
          >
            <MenuItem value="">—</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p._id} value={p._id}>
                {p.title?.fa || p.title?.en || 'پروژه'}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="کلینیک"
            value={form.clinicId}
            onChange={(e) => setForm((p) => ({ ...p, clinicId: e.target.value }))}
            fullWidth
            disabled={!form.projectId}
            error={!!errors.clinicId}
            helperText={errors.clinicId || ''}
          >
            <MenuItem value="">—</MenuItem>
            {clinics.map((c) => (
              <MenuItem key={c._id} value={c._id}>
                {c.title?.fa || c.title?.en || 'کلینیک'}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="توضیحات"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>انصراف</Button>
          <Button variant="contained" onClick={handleSave}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectsPage;
